import { XMarkIcon } from "@heroicons/react/24/outline"
import { CheckBadgeIcon } from "@heroicons/react/24/solid"
import React, { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import { urqlClient } from "../../../utils/utils"

const APPOINTMENTS_LAST_24H_COUNT_QUERY = `
  query AppointmentsLast24hCount {
    appointmentsLast24hCount
  }
`

// Add a url here if you want to redirect to a different page when the card is clicked.
const CARD_CLICK_URL = ""

const AppointmentSocialProofNotification = ({ isMobile }) => {
  const ENTER_ANIMATION_MS = 400
  const EXIT_ANIMATION_MS = 500
  const SOCIAL_PROOF_HIDE_EVENT = "appointmentSocialProofNotification:hide"
  const SOCIAL_PROOF_DISMISS_ALL_EVENT = "socialProofNotifications:dismissAll"
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [appointmentsCount, setAppointmentsCount] = useState(0)
  const [animatedAppointmentsCount, setAnimatedAppointmentsCount] = useState(1)
  const timeoutRef = useRef(null)
  const showDelayRef = useRef(null)
  const countStartDelayRef = useRef(null)
  const countAnimationRef = useRef(null)

  useEffect(() => {
    if (dismissed) return
    let isCancelled = false

    const clearTimers = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (showDelayRef.current) clearTimeout(showDelayRef.current)
      if (countStartDelayRef.current) clearTimeout(countStartDelayRef.current)
      if (countAnimationRef.current) clearInterval(countAnimationRef.current)
    }

    const startCountAnimation = (targetCount) => {
      const startCount = 1
      const totalDurationMs = 1000
      const minTickMs = 40
      const totalIncrements = Math.max(0, targetCount - startCount)

      if (totalIncrements === 0) {
        setAnimatedAppointmentsCount(targetCount)
        return
      }

      // Keep total animation time <= 1.5s by adjusting increment size and tick cadence.
      const maxTicks = Math.max(1, Math.floor(totalDurationMs / minTickMs))
      const incrementPerTick = Math.max(1, Math.ceil(totalIncrements / maxTicks))
      const ticksNeeded = Math.ceil(totalIncrements / incrementPerTick)
      const tickMs = totalDurationMs / ticksNeeded

      if (countAnimationRef.current) clearInterval(countAnimationRef.current)
      setAnimatedAppointmentsCount(startCount)
      countAnimationRef.current = setInterval(() => {
        setAnimatedAppointmentsCount((prevCount) => {
          const nextCount = prevCount + incrementPerTick
          if (nextCount >= targetCount) {
            clearInterval(countAnimationRef.current)
            countAnimationRef.current = null
            return targetCount
          }
          return nextCount
        })
      }, tickMs)
    }

    const fetchAppointmentsCount = async () => {
      try {
        const result = await urqlClient.query(APPOINTMENTS_LAST_24H_COUNT_QUERY).toPromise()
        const count = Number(result?.data?.appointmentsLast24hCount)
        const parsedCount = Math.floor(count)
        if (!isCancelled && Number.isFinite(parsedCount) && parsedCount > 1) {
          setAppointmentsCount(parsedCount)
          setAnimatedAppointmentsCount(1)
          setVisible(false)
          showDelayRef.current = setTimeout(() => {
            if (isCancelled) return
            setVisible(true)
            countStartDelayRef.current = setTimeout(() => {
              if (!isCancelled) startCountAnimation(parsedCount)
            }, ENTER_ANIMATION_MS)
            timeoutRef.current = setTimeout(() => {
              setVisible(false)
              if (typeof document !== "undefined") {
                document.dispatchEvent(
                  new CustomEvent(SOCIAL_PROOF_HIDE_EVENT, {
                    detail: { hiddenAt: Date.now(), exitMs: EXIT_ANIMATION_MS }
                  })
                )
              }
            }, 6000)
          }, 100)
        }
      } catch (e) {
        // Ignore query failures so the page still renders normally.
      }
    }

    fetchAppointmentsCount()

    return () => {
      isCancelled = true
      clearTimers()
    }
  }, [dismissed])

  const peopleLabel = appointmentsCount === 1 ? "person" : "people"
  const isCardClickable = CARD_CLICK_URL.trim().length > 0
  const navigateToCustomURL = () => {
    if (!isCardClickable) return
    window.location.assign(CARD_CLICK_URL)
  }

  // For accessibility, allow the card to be navigated with the keyboard.
  const onCardKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      navigateToCustomURL()
    }
  }

  const closeNotification = (e) => {
    e.stopPropagation()
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (showDelayRef.current) clearTimeout(showDelayRef.current)
    if (countStartDelayRef.current) clearTimeout(countStartDelayRef.current)
    if (countAnimationRef.current) clearInterval(countAnimationRef.current)
    setVisible(false)
    setDismissed(true)
    if (typeof document !== "undefined") {
      document.dispatchEvent(new CustomEvent(SOCIAL_PROOF_DISMISS_ALL_EVENT))
    }
  }

  if (dismissed || appointmentsCount <= 1) return null

  return (
    <div
      role={isCardClickable ? "link" : undefined}
      tabIndex={isCardClickable && visible ? 0 : -1}
      onClick={isCardClickable ? navigateToCustomURL : undefined}
      onKeyDown={isCardClickable ? onCardKeyDown : undefined}
      aria-label={isCardClickable ? "Open social proof link" : undefined}
      className={twMerge(
        "fixed z-[999998] rounded-full border border-[#e3e8ee] bg-white/95 p-4 shadow-[0_20px_40px_rgba(0,0,0,0.12)] backdrop-blur-sm transition-all duration-500",
        isCardClickable ? "cursor-pointer hover:shadow-[0_22px_44px_rgba(0,0,0,0.16)]" : "",
        visible ? "pointer-events-auto" : "pointer-events-none",
        isMobile ? "inset-x-4 bottom-4" : "bottom-3 left-3 w-[380px]",
        isMobile
          ? visible
            ? "translate-y-0 opacity-100"
            : "translate-y-[140%] opacity-0"
          : visible
          ? "translate-x-0 opacity-100"
          : "translate-x-[-120%] opacity-0"
      )}>
      <button
        type="button"
        aria-label="Close notification"
        onClick={closeNotification}
        className="absolute right-3 top-3 inline-flex size-5 items-center justify-center rounded-full text-[#9aa3af] hover:bg-[#f0f2f5]">
        <XMarkIcon className="size-3.5" />
      </button>
      <div className="flex items-center gap-3">
        <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[#ffebee]">
          <svg width="54px" height="54px" viewBox="0 0 69 69" version="1.1" xmlns="http://www.w3.org/2000/svg">
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <g>
                <circle id="Oval-3" fill="transparent" cx="34.5" cy="34.5" r="34.5" />
                <path
                  d="M27.9375,9 C29.1236111,23.0783576 17,27.9251979 17,40.3252759 C17,48.7480073 22.9675,55.9369975 34.5,55.9996637 C46.0325,56.06233 52,47.3556423 52,38.4609559 C52,30.3515556 47.9905556,22.7043186 40.4266667,17.9495193 C42.2233333,23.0548578 39.8316667,27.7176161 37.5080556,29.3234379 C37.6441667,22.7885264 35.3205556,13.0909291 27.9375,9 Z"
                  id="Shape"
                  fill="#F44336"
                />
                <path
                  d="M36.2776958,34 C43.4460962,41.978 39.0438775,52 33.2862488,52 C29.7831823,52 27.9810598,49.47 28.0001501,46.846 C28.0364216,41.98 33.2251599,41.976 36.2776958,34 Z"
                  id="Path"
                  fill="#D50000"
                />
              </g>
            </g>
          </svg>
        </div>
        <div className="min-w-0">
          <div className="inline-flex rounded-md bg-[#ececee] px-2 py-px">
            <p className="font-manrope text-[15px] font-extrabold leading-[20px] text-[#30363f]">
              {appointmentsCount > 1 ? animatedAppointmentsCount : appointmentsCount} {peopleLabel}
            </p>
          </div>
          <p className="mt-1 font-manrope text-[15px] font-medium leading-[20px] text-[#6b7280]">booked a session</p>
          <div className="mt-[2px] flex items-center justify-between gap-2">
            <p className="font-manrope text-[12px] font-medium leading-[16px] text-[#8f96a3]">in the last 24 hours</p>
            <div className="flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold leading-[16px] text-[#1a77db]">
              <CheckBadgeIcon className="h-[14px] w-[14px]" />
              <a
                href="https://useproof.com/"
                target="_blank"
                rel="noreferrer noopener"
                onClick={(e) => e.stopPropagation()}>
                verified by Proof
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppointmentSocialProofNotification
